import { type Calculation, calculateValueFromPoints } from "@suse-o11y/calculate-value-from-points";
import { cx } from "@suse-o11y/class-variance";
import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { formatValue } from "@suse-o11y/format-value";
import type { FormatterUnit } from "@suse-o11y/formatter-unit";
import { max } from "@suse-o11y/max";
import { prop } from "@suse-o11y/prop";
import { px } from "@suse-o11y/px";
import { take } from "@suse-o11y/take";
import React from "react";

import type { TimeSeries } from "../../TimeSeriesChart/time-series";

import { calculateGridLayout } from "./calculate-grid-layout";

const CELL_CLASSNAME = cx("-mr-px -mb-px border-grey-400 border-r border-b border-dashed p-(--padding)");

export interface ChartsGridProps {
	className?: string;
	width: number;
	height: number;
	minCellSize: number;
	calculation: Calculation;
	decimalPlaces: undefined | number;
	unit: FormatterUnit;
	timeSeries: ReadonlyArray<TimeSeries>;
	showAllDialog: React.ReactNode;
	autoValueFontSize: (width: number, height: number, textLength: number) => number;
	children: (series: TimeSeries, cell: { width: number; height: number; fontSize: number }) => React.ReactNode;
}

export const ChartsGrid = React.forwardRef<React.ComponentRef<"div">, ChartsGridProps>(
	(
		{
			className,
			width,
			height,
			minCellSize,
			calculation,
			decimalPlaces,
			unit,
			timeSeries,
			showAllDialog,
			autoValueFontSize,
			children,
		},
		ref,
	) => {
		const { rows, cols, padding } = calculateGridLayout({
			width,
			height,
			minCellSize,
			count: timeSeries.length,
		});

		const cellWidth = width / cols - 2 * padding;
		const cellHeight = height / rows - 2 * padding;

		const limit = showAllDialog != null && rows * cols < timeSeries.length ? rows * cols - 1 : timeSeries.length;
		const overflowText = limit < timeSeries.length ? `+${timeSeries.length - limit} more` : null;

		const visibleSeries = React.useMemo(() => take(timeSeries, limit), [timeSeries, limit]);

		const longestFormattedValueLength = React.useMemo(() => {
			const formattedValues = visibleSeries.map(({ values }) => {
				const value = calculateValueFromPoints(calculation, values);

				return formatValue(value, { precision: decimalPlaces, unit });
			});

			return max(0, formattedValues.map(prop("length")));
		}, [visibleSeries, calculation, decimalPlaces, unit]);

		const fontSize = autoValueFontSize(cellWidth, cellHeight, max(longestFormattedValueLength, overflowText?.length));

		return (
			<div
				className={cx(
					"grid grid-flow-row auto-rows-fr grid-cols-[repeat(var(--columns),minmax(0,1fr))] overflow-clip",
					className,
				)}
				ref={ref}
				style={
					{
						"--columns": cols,
						"--padding": px(padding),
						"--font-size": px(fontSize),
					} as React.CSSProperties
				}
			>
				{visibleSeries.map((series, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: there is no better key than index here
					<div className={CELL_CLASSNAME} key={index}>
						{children(series, { width: cellWidth, height: cellHeight, fontSize })}
					</div>
				))}

				{overflowText != null && showAllDialog != null && (
					<DialogBoundary>
						<DialogTrigger className={cx(CELL_CLASSNAME, "link-secondary link text-(size:--font-size) text-center")}>
							{overflowText}
						</DialogTrigger>

						{showAllDialog}
					</DialogBoundary>
				)}
			</div>
		);
	},
);
